import { useChartColors } from '../lib/accessibility'
import { apiError, errStatus } from '../lib/errors'
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, ShoppingBag, X, DollarSign, Package, TrendingUp, ExternalLink, CheckCircle2 } from 'lucide-react'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { commerceApi, varietiesApi, paymentsApi, containersApi, shippingApi, type Product, type Order, type HarvestAvailable, type PODCatalogItem, type Container, type ShipRate, type ShipAddress, type ShipParcel } from '../lib/api'
import { PhotoManager } from '../components/shared/PhotoManager'

const C = {
  bg:      'var(--color-bg)',
  card:    'var(--color-bg-card)',
  card2:   '#1A2314',
  border:  'var(--color-border)',
  amber:   'var(--color-primary)',
  green:   '#4CAF50',
  text:    'var(--color-text)',
  muted:   'var(--color-text-muted)',
  faint:   'var(--color-text-faint)',
  danger:  '#F87171',
}
const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card2,
  border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
}
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: C.muted,
  marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.06em',
}

function ProductModal({ product, onClose }: { product: Product | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isEdit = !!product
  const [form, setForm] = useState({
    name: product?.name ?? '', price: product?.price?.toString() ?? '',
    available_qty: product?.available_qty?.toString() ?? '0',
    variety_id: product?.variety_id ?? '', is_available: product?.is_available ?? true,
  })
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(f => ({ ...f, [k]: v }))

  const { data: varietiesData } = useQuery({
    queryKey: ['varieties', 'all'],
    queryFn: () => varietiesApi.list().then(r => r.data),
  })

  const save = useMutation({
    mutationFn: (data: Partial<Product>) =>
      (isEdit ? commerceApi.updateProduct(product!.id, data) : commerceApi.createProduct(data)) as Promise<unknown>,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commerce-products'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed')),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) { setError('Product name required'); return }
    if (!form.price) { setError('Price required'); return }
    save.mutate({
      name: form.name, price: parseFloat(form.price),
      available_qty: parseInt(form.available_qty) || 0,
      variety_id: form.variety_id || undefined, is_available: form.is_available,
    })
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 440, maxWidth: '95vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{isEdit ? 'Edit product' : 'Add product'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={18} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><label style={lbl}>Product name *</label><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Keitt Mango — 10lb box" required /></div>
          <div>
            <label style={lbl}>Variety</label>
            <select style={{ ...inp, cursor: 'pointer' }} value={form.variety_id} onChange={e => set('variety_id', e.target.value)}>
              <option value="">— Select variety —</option>
              {(varietiesData?.varieties ?? []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Price ($) *</label><input style={inp} type="number" min={0} step={0.01} value={form.price} onChange={e => set('price', e.target.value)} placeholder="24.99" required /></div>
            <div><label style={lbl}>Available qty</label><input style={inp} type="number" min={0} value={form.available_qty} onChange={e => set('available_qty', e.target.value)} /></div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.muted }}>
            <input type="checkbox" checked={form.is_available} onChange={e => set('is_available', e.target.checked)} />
            Available for sale on marketing site
          </label>
          {isEdit && product && (
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              <PhotoManager resourceType="product" resourceId={product.id} label="Product photos (shown on your storefront)" />
            </div>
          )}
          {error && <div style={{ fontSize: 12, color: C.danger, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={save.isPending} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {save.isPending ? 'Saving…' : isEdit ? 'Save' : 'Add product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/** Build a rolling 8-month revenue chart from orders */
function buildRevenueChart(orders: Order[]) {
  const now   = new Date()
  const months: { label: string; revenue: number; count: number }[] = []
  for (let i = 7; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    months.push({
      label: d.toLocaleDateString([], { month: 'short', year: '2-digit' }),
      revenue: 0,
      count: 0,
    })
  }
  orders.forEach(o => {
    const d  = new Date(o.created_at)
    const key = d.toLocaleDateString([], { month: 'short', year: '2-digit' })
    const slot = months.find(m => m.label === key)
    if (slot) { slot.revenue += o.total_amount; slot.count++ }
  })
  return months
}

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  reserved:  { label: 'New',       color: C.amber,   bg: 'color-mix(in srgb, var(--color-primary) 16%, transparent)' },
  confirmed: { label: 'Confirmed', color: '#5B9BD5', bg: 'rgba(91,155,213,0.15)' },
  ready:     { label: 'Ready',     color: C.green,   bg: 'rgba(76,175,80,0.15)' },
  shipped:   { label: 'Shipped',   color: '#26A69A', bg: 'rgba(38,166,154,0.15)' },
  completed: { label: 'Completed', color: C.muted,   bg: 'rgba(127,127,127,0.12)' },
  cancelled: { label: 'Cancelled', color: C.danger,  bg: 'rgba(248,113,113,0.12)' },
}
const FULFILLMENT_LABEL: Record<string, string> = { pickup: 'Pickup', delivery: 'Delivery', other: 'Other' }

interface OrderAction { label: string; to: string; danger?: boolean }

/** A single storefront order with its fulfillment workflow controls. */
function OrderCard({ order, onUpdate, busy }: {
  order: Order
  onUpdate: (id: string, body: { status: string; tracking_info?: string; note?: string }) => void
  busy: boolean
}) {
  const status = order.status || 'reserved'
  const fulfillment = order.fulfillment_method || ''
  const meta = STATUS_META[status] ?? STATUS_META.reserved
  const active = status !== 'completed' && status !== 'cancelled'
  const [open, setOpen] = useState(active)
  const [tracking, setTracking] = useState(order.tracking_info ?? '')
  const [note, setNote] = useState('')

  const actions: OrderAction[] = []
  if (status === 'reserved') {
    actions.push({ label: 'Confirm order', to: 'confirmed' })
    actions.push({ label: 'Cancel', to: 'cancelled', danger: true })
  } else if (status === 'confirmed') {
    if (fulfillment !== 'delivery') actions.push({ label: 'Mark ready for pickup', to: 'ready' })
    if (fulfillment !== 'pickup')   actions.push({ label: 'Mark shipped', to: 'shipped' })
    actions.push({ label: 'Cancel', to: 'cancelled', danger: true })
  } else if (status === 'ready' || status === 'shipped') {
    actions.push({ label: 'Mark completed', to: 'completed' })
  }
  const showTracking = status === 'confirmed' ? fulfillment !== 'pickup' : status === 'shipped'

  function run(a: OrderAction) {
    onUpdate(order.id, {
      status: a.to,
      tracking_info: showTracking && tracking.trim() ? tracking.trim() : undefined,
      note: note.trim() || undefined,
    })
    setNote('')
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
      {/* Summary row */}
      <div onClick={() => setOpen(o => !o)} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, color: meta.color, background: meta.bg, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>{meta.label}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>
            #{order.id.slice(-8).toUpperCase()}
            {order.customer_name && <span style={{ fontWeight: 400, color: C.muted }}> · {order.customer_name}</span>}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 11, color: C.faint }}>
            {new Date(order.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            {fulfillment && <span> · {FULFILLMENT_LABEL[fulfillment] ?? fulfillment}</span>}
          </p>
        </div>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text, flexShrink: 0 }}>${order.total_amount.toFixed(2)}</p>
      </div>

      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 14 }}>
          {/* Customer */}
          {(order.customer_email || order.customer_phone) && (
            <div style={{ fontSize: 12, color: C.muted }}>
              {order.customer_email && <span>{order.customer_email}</span>}
              {order.customer_email && order.customer_phone && <span style={{ color: C.faint }}> · </span>}
              {order.customer_phone && <span>{order.customer_phone}</span>}
            </div>
          )}

          {/* Line items */}
          {order.items && order.items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {order.items.map((it, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted }}>
                  <span>{it.quantity}× {it.name}</span>
                  <span>${it.line_total.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {order.notes && (
            <div style={{ fontSize: 12, color: C.muted, background: C.card2, padding: '8px 12px', borderRadius: 8 }}>
              <span style={{ color: C.faint }}>Customer note: </span>{order.notes}
            </div>
          )}

          {order.tracking_info && status === 'shipped' && (
            <div style={{ fontSize: 12, color: C.green }}>Tracking: {order.tracking_info}</div>
          )}

          {/* Tracking + admin note inputs while actionable */}
          {actions.length > 0 && (
            <>
              {showTracking && (
                <div><label style={lbl}>Tracking # (optional)</label><input style={inp} value={tracking} onChange={e => setTracking(e.target.value)} placeholder="1Z…" /></div>
              )}
              <div><label style={lbl}>Internal note (optional)</label><input style={inp} value={note} onChange={e => setNote(e.target.value)} placeholder="Note added to this update" /></div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {actions.map(a => (
                  <button key={a.to} onClick={() => run(a)} disabled={busy}
                    style={{
                      padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer',
                      border: a.danger ? `1px solid ${C.danger}` : 'none',
                      background: a.danger ? 'transparent' : C.amber,
                      color: a.danger ? C.danger : C.bg, opacity: busy ? 0.6 : 1,
                    }}>{a.label}</button>
                ))}
              </div>
            </>
          )}
          {order.admin_note && (
            <div style={{ fontSize: 11, color: C.faint }}>Last note: {order.admin_note}</div>
          )}

          {/* Pick list + shipping labels (shippable orders) */}
          {active && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => printPickList(order)}
                style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${C.border}`, background: 'transparent', color: C.text }}>
                🖨 Pick list
              </button>
            </div>
          )}
          {fulfillment !== 'pickup' && active && (
            <ShipSection order={order} canBuy={status === 'confirmed' || status === 'ready'}
              onShipped={(tracking) => onUpdate(order.id, { status: 'shipped', tracking_info: tracking })} />
          )}

          {/* Traceability: which containers filled this order (downstream recall chain) */}
          <ContainerLinkSection orderId={order.id} />
        </div>
      )}
    </div>
  )
}

/** Opens a printable pick list (order line items) in a new window. Self-contained —
 *  no global print CSS, so it never interferes with other print flows. */
function printPickList(order: Order) {
  const rows = (order.items ?? []).map(it => `<tr><td style="padding:6px 10px;border-bottom:1px solid #ddd">${it.quantity}</td><td style="padding:6px 10px;border-bottom:1px solid #ddd">${escapeHTML(it.name)}</td></tr>`).join('')
  const w = window.open('', '_blank', 'width=560,height=720')
  if (!w) return
  w.document.write(`<!doctype html><html><head><title>Pick list ${order.id.slice(-8).toUpperCase()}</title>
    <style>body{font-family:system-ui,sans-serif;margin:32px;color:#111}h1{font-size:18px;margin:0 0 4px}
    .meta{font-size:13px;color:#555;margin:0 0 16px}table{border-collapse:collapse;width:100%}
    th{text-align:left;padding:6px 10px;border-bottom:2px solid #111;font-size:12px}</style></head><body>
    <h1>Pick list — #${order.id.slice(-8).toUpperCase()}</h1>
    <p class="meta">${escapeHTML(order.customer_name || '')}${order.fulfillment_method ? ' · ' + escapeHTML(order.fulfillment_method) : ''} · ${new Date(order.created_at).toLocaleDateString()}</p>
    <table><thead><tr><th style="width:60px">Qty</th><th>Item</th></tr></thead><tbody>${rows || '<tr><td colspan="2" style="padding:10px;color:#888">No line items</td></tr>'}</tbody></table>
    </body></html>`)
  w.document.close()
  w.focus()
  setTimeout(() => w.print(), 250)
}
function escapeHTML(s: string) {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string))
}

/** Rate-shop + buy a carrier shipping label for a delivery order (EasyPost, §55.10).
 *  On purchase it opens the label to print + flips the order to 'shipped' (with tracking)
 *  via the parent's onShipped, which fires the buyer's shipped email. Gated server-side by
 *  FlagShipping (Grower+) + the platform EasyPost key — errors surface inline. */
function ShipSection({ order, canBuy, onShipped }: { order: Order; canBuy: boolean; onShipped: (tracking: string) => void }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [to, setTo] = useState<ShipAddress>({ name: order.customer_name ?? '', phone: order.customer_phone ?? '', country: 'US' })
  const [weightLb, setWeightLb] = useState('')
  const [dims, setDims] = useState({ l: '', w: '', h: '' })
  const [format, setFormat] = useState('PDF')
  const [rates, setRates] = useState<ShipRate[] | null>(null)
  const [shipmentId, setShipmentId] = useState('')
  const [chosen, setChosen] = useState('')
  const [error, setError] = useState('')

  const { data: existing } = useQuery({
    queryKey: ['shipments', order.id],
    queryFn: () => shippingApi.forOrder(order.id).then(r => r.data.shipments),
  })

  const parcel = (): ShipParcel => ({
    weight_oz: Math.round((parseFloat(weightLb) || 0) * 16 * 100) / 100,
    length_in: parseFloat(dims.l) || undefined,
    width_in: parseFloat(dims.w) || undefined,
    height_in: parseFloat(dims.h) || undefined,
  })

  const rate = useMutation({
    mutationFn: () => shippingApi.rates({ order_id: order.id, to, parcel: parcel(), label_format: format }).then(r => r.data),
    onSuccess: (d) => { setRates(d.rates); setShipmentId(d.shipment_id); setChosen(d.rates[0]?.id ?? ''); setError(d.warning ?? '') },
    onError: (e) => { setRates(null); setError(apiError(e, 'Could not get rates')) },
  })
  const buy = useMutation({
    mutationFn: () => shippingApi.buyLabel({ order_id: order.id, shipment_id: shipmentId, rate_id: chosen, label_format: format, to, parcel: parcel() }).then(r => r.data),
    onSuccess: (sh) => {
      if (sh.label_url) window.open(sh.label_url, '_blank')
      qc.invalidateQueries({ queryKey: ['shipments', order.id] })
      onShipped(sh.tracking_code)
      setOpen(false); setRates(null)
    },
    onError: (e) => setError(apiError(e, 'Could not buy label')),
  })

  // Nothing to render when there's no label to reprint and buying isn't available yet.
  if (!canBuy && (!existing || existing.length === 0)) return null

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
      {existing && existing.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
          {existing.map(sh => (
            <div key={sh.id} style={{ fontSize: 12, color: C.muted, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>📦 {sh.carrier} {sh.service} · {sh.tracking_code || '—'} · ${sh.cost.toFixed(2)}</span>
              {sh.label_url && <a href={sh.label_url} target="_blank" rel="noreferrer" style={{ color: C.amber }}>Reprint label</a>}
            </div>
          ))}
        </div>
      )}
      {!canBuy ? null : !open ? (
        <button onClick={() => setOpen(true)}
          style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${C.border}`, background: 'transparent', color: C.text }}>
          🚚 Buy shipping label
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: C.card2, padding: 12, borderRadius: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Ship to</div>
          <input style={inp} placeholder="Recipient name" value={to.name ?? ''} onChange={e => setTo({ ...to, name: e.target.value })} />
          <input style={inp} placeholder="Street address" value={to.street1 ?? ''} onChange={e => setTo({ ...to, street1: e.target.value })} />
          <input style={inp} placeholder="Apt / suite (optional)" value={to.street2 ?? ''} onChange={e => setTo({ ...to, street2: e.target.value })} />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
            <input style={inp} placeholder="City" value={to.city ?? ''} onChange={e => setTo({ ...to, city: e.target.value })} />
            <input style={inp} placeholder="State" value={to.state ?? ''} onChange={e => setTo({ ...to, state: e.target.value })} />
            <input style={inp} placeholder="ZIP" value={to.zip ?? ''} onChange={e => setTo({ ...to, zip: e.target.value })} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginTop: 4 }}>Parcel</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
            <input style={inp} type="number" step="0.1" placeholder="Weight (lb)" value={weightLb} onChange={e => setWeightLb(e.target.value)} />
            <input style={inp} type="number" step="0.1" placeholder="L (in)" value={dims.l} onChange={e => setDims({ ...dims, l: e.target.value })} />
            <input style={inp} type="number" step="0.1" placeholder="W (in)" value={dims.w} onChange={e => setDims({ ...dims, w: e.target.value })} />
            <input style={inp} type="number" step="0.1" placeholder="H (in)" value={dims.h} onChange={e => setDims({ ...dims, h: e.target.value })} />
          </div>
          <div>
            <label style={lbl}>Label format</label>
            <select style={inp} value={format} onChange={e => setFormat(e.target.value)}>
              <option value="PDF">Standard (PDF)</option>
              <option value="ZPL">Thermal (ZPL — Rollo / Zebra)</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setError(''); rate.mutate() }} disabled={rate.isPending}
              style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: C.amber, color: C.bg, opacity: rate.isPending ? 0.6 : 1 }}>
              {rate.isPending ? 'Getting rates…' : 'Get rates'}
            </button>
            <button onClick={() => { setOpen(false); setRates(null); setError('') }}
              style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${C.border}`, background: 'transparent', color: C.text }}>
              Cancel
            </button>
          </div>
          {rates && rates.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {rates.map(r => (
                <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.text, cursor: 'pointer' }}>
                  <input type="radio" name={`rate-${order.id}`} checked={chosen === r.id} onChange={() => setChosen(r.id)} />
                  <span>{r.carrier} {r.service} — <strong>${r.rate.toFixed(2)}</strong>{r.delivery_days ? ` · ${r.delivery_days}d` : ''}</span>
                </label>
              ))}
              <button onClick={() => { setError(''); buy.mutate() }} disabled={buy.isPending || !chosen}
                style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: C.green, color: '#fff', opacity: (buy.isPending || !chosen) ? 0.6 : 1, marginTop: 4, alignSelf: 'flex-start' }}>
                {buy.isPending ? 'Buying…' : 'Buy label & mark shipped'}
              </button>
            </div>
          )}
          {error && <p style={{ margin: 0, fontSize: 12, color: C.danger }}>{error}</p>}
        </div>
      )}
    </div>
  )
}

/** Records which physical container(s) an order was packed from, so a recall on a bad
 *  container can trace forward to this order + customer (§164 / grafty-api §210). */
function ContainerLinkSection({ orderId }: { orderId: string }) {
  const qc = useQueryClient()
  const [pick, setPick] = useState('')
  const { data: linked } = useQuery({
    queryKey: ['order-containers', orderId],
    queryFn: () => containersApi.orderContainers(orderId).then(r => r.data.containers),
  })
  const { data: all } = useQuery({
    queryKey: ['containers'],
    queryFn: () => containersApi.list().then(r => r.data.containers),
  })
  const linkedIds = new Set((linked ?? []).map(c => c.id))
  const options = (all ?? []).filter(c => !linkedIds.has(c.id))
  const link = useMutation({
    mutationFn: (containerId: string) => containersApi.linkOrder(orderId, containerId),
    onSuccess: () => { setPick(''); qc.invalidateQueries({ queryKey: ['order-containers', orderId] }) },
  })
  const unlink = useMutation({
    mutationFn: (containerId: string) => containersApi.unlinkOrder(orderId, containerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-containers', orderId] }),
  })
  const label = (c: Container) => c.label || (c.container_type ? c.container_type[0].toUpperCase() + c.container_type.slice(1) : 'Container')

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
      <label style={lbl}>Packed from (for traceability / recall)</label>
      {(linked ?? []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {(linked ?? []).map(c => (
            <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, padding: '3px 8px', borderRadius: 999, background: C.card2, color: C.text }}>
              {label(c)}
              <button onClick={() => unlink.mutate(c.id)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint, padding: 0, display: 'inline-flex' }}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <select value={pick} onChange={e => setPick(e.target.value)} style={{ ...inp, flex: 1 }}>
          <option value="">{options.length ? 'Add a container…' : 'No containers to add'}</option>
          {options.map(c => <option key={c.id} value={c.id}>{label(c)}{c.variety_name ? ` · ${c.variety_name}` : ''}</option>)}
        </select>
        <button onClick={() => pick && link.mutate(pick)} disabled={!pick || link.isPending}
          style={{ padding: '8px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, border: `1px solid ${C.border}`, background: 'transparent', color: C.text, cursor: pick ? 'pointer' : 'default', opacity: pick ? 1 : 0.6 }}>
          Attach
        </button>
      </div>
    </div>
  )
}

/** Push freshly-picked produce into the storefront from recent harvests. */
function HarvestFeedPanel() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<string | null>(null)
  const [qty, setQty] = useState('')
  const [price, setPrice] = useState('')
  const { data: rows = [] } = useQuery({
    queryKey: ['commerce-harvest-available'],
    queryFn: () => commerceApi.harvestAvailable().then(r => r.data.harvest ?? []),
  })
  const restock = useMutation({
    mutationFn: (body: { variety_id: string; quantity: number; price?: number }) => commerceApi.restock(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commerce-products'] })
      qc.invalidateQueries({ queryKey: ['commerce-harvest-available'] })
      setEditing(null)
    },
  })

  if (rows.length === 0) return null

  function open(row: HarvestAvailable) {
    setEditing(row.variety_id)
    setQty(Math.round(row.harvested_kg).toString())
    setPrice(row.suggested_price > 0 ? row.suggested_price.toFixed(2) : '')
  }
  function submit(row: HarvestAvailable) {
    const q = parseInt(qty)
    if (!q || q <= 0) return
    const p = parseFloat(price)
    restock.mutate({ variety_id: row.variety_id, quantity: q, price: isNaN(p) ? undefined : p })
  }

  return (
    <div style={{ marginBottom: 16, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Package size={14} style={{ color: C.green }} />
        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Send picked harvest to the shop</h2>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: C.faint }}>last 60 days</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map(row => (
          <div key={row.variety_id} style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>{row.variety_name}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: C.faint }}>
                  {row.harvested_kg.toFixed(1)} kg picked
                  {row.listed_qty > 0 && <span> · {row.listed_qty} listed now</span>}
                  {row.suggested_price > 0 && <span> · ${row.suggested_price.toFixed(2)}/kg suggested</span>}
                </p>
              </div>
              {editing !== row.variety_id && (
                <button onClick={() => open(row)} style={{ padding: '6px 12px', borderRadius: 7, background: C.green, border: 'none', color: '#0E1209', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Send to shop</button>
              )}
            </div>
            {editing === row.variety_id && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 8 }}>
                <div><label style={lbl}>Qty</label><input style={{ ...inp, width: 80 }} type="number" min={1} value={qty} onChange={e => setQty(e.target.value)} /></div>
                <div><label style={lbl}>Price ($)</label><input style={{ ...inp, width: 90 }} type="number" min={0} step={0.01} value={price} onChange={e => setPrice(e.target.value)} placeholder="per unit" /></div>
                <button onClick={() => submit(row)} disabled={restock.isPending} style={{ padding: '8px 14px', borderRadius: 7, background: C.amber, border: 'none', color: C.bg, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{restock.isPending ? 'Listing…' : 'List it'}</button>
                <button onClick={() => setEditing(null)} style={{ padding: '8px 12px', borderRadius: 7, background: C.card2, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
              </div>
            )}
            {restock.isError && editing === row.variety_id && (
              <p style={{ margin: '6px 0 0', fontSize: 11, color: C.danger }}>{apiError(restock.error, 'Failed')}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Merch (print-on-demand) tab ──────────────────────────────────────────────
function MerchCreateModal({ item, onClose }: { item: PODCatalogItem; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState(item.name)
  const [price, setPrice] = useState(item.suggested_retail_usd.toFixed(2))
  const [design, setDesign] = useState('')
  const [error, setError] = useState<string | null>(null)
  const margin = (parseFloat(price) || 0) - item.base_cost_usd

  const save = useMutation({
    mutationFn: () => commerceApi.createMerchProduct({ catalog_id: item.id, name: name.trim(), price: parseFloat(price) || undefined, design_image_url: design.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commerce-products'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed')),
  })

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 440, maxWidth: '95vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>List {item.name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><label style={lbl}>Product name</label><input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder={item.name} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Your price ($)</label><input style={inp} type="number" min={item.base_cost_usd} step={0.01} value={price} onChange={e => setPrice(e.target.value)} /></div>
            <div>
              <label style={lbl}>Your margin</label>
              <div style={{ ...inp, color: margin > 0 ? C.green : C.danger, fontWeight: 700 }}>${margin.toFixed(2)}</div>
            </div>
          </div>
          <p style={{ margin: '-4px 0 0', fontSize: 11, color: C.faint }}>POD base cost ${item.base_cost_usd.toFixed(2)} · suggested ${item.suggested_retail_usd.toFixed(2)} · Grafty takes the print margin</p>
          <div><label style={lbl}>Design image URL (optional)</label><input style={inp} value={design} onChange={e => setDesign(e.target.value)} placeholder="https://…/your-logo.png" /></div>
          {error && <div style={{ fontSize: 12, color: C.danger, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              {save.isPending ? 'Listing…' : 'List on storefront'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function MerchTab({ products }: { products: Product[] }) {
  const [picked, setPicked] = useState<PODCatalogItem | null>(null)
  const { data: catalog = [], isLoading, error } = useQuery({
    queryKey: ['merch-catalog'],
    queryFn: () => commerceApi.merchCatalog().then(r => r.data.catalog ?? []),
  })
  const merch = products.filter(p => p.kind === 'merch')

  if (errStatus(error) === 402) {
    return <div style={{ textAlign: 'center', padding: '48px 0', color: C.muted, fontSize: 14 }}>Merch storefront isn't enabled for this account.</div>
  }

  return (
    <div>
      <div style={{ marginBottom: 18, padding: '12px 16px', background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)', borderRadius: 10, border: `1px solid ${C.border}` }}>
        <p style={{ margin: 0, fontSize: 13, color: C.text, fontWeight: 600 }}>🎽 Free merch storefront</p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: C.muted }}>Put your farm's design on print-on-demand products. No inventory, no upfront cost — items print &amp; ship when ordered. Set your price above the base cost and keep the margin.</p>
      </div>

      {/* Existing merch products */}
      {merch.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Your merch ({merch.length})</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {merch.map(p => (
              <div key={p.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.is_available ? C.green : C.faint, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>{p.name}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: C.faint }}>
                    ${p.price.toFixed(2)}
                    {typeof p.base_cost_usd === 'number' && p.base_cost_usd > 0 && <span style={{ color: C.green }}> · ${(p.price - p.base_cost_usd).toFixed(2)} margin</span>}
                    {p.design_image_url && <span> · design set</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Catalog to pick from */}
      <p style={{ fontSize: 11, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Add a product</p>
      {isLoading ? <div style={{ color: C.faint, fontSize: 13 }}>Loading catalog…</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {catalog.map(item => (
            <div key={item.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>{item.name}</p>
              <p style={{ margin: 0, fontSize: 11, color: C.faint, textTransform: 'capitalize' }}>{item.category}</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: C.muted }}>Base ${item.base_cost_usd.toFixed(2)} · suggest ${item.suggested_retail_usd.toFixed(2)}</p>
              <button onClick={() => setPicked(item)} style={{ marginTop: 4, padding: '7px 0', borderRadius: 7, background: C.amber, border: 'none', color: C.bg, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Add to shop</button>
            </div>
          ))}
        </div>
      )}

      {picked && <MerchCreateModal item={picked} onClose={() => setPicked(null)} />}
    </div>
  )
}

/** Stripe Connect status + onboarding for storefront card payments. */
function PaymentsBanner() {
  const { data, refetch } = useQuery({
    queryKey: ['payments-status'],
    queryFn: () => paymentsApi.status().then(r => r.data),
  })
  const connect = useMutation({
    mutationFn: () => paymentsApi.connect(),
    onSuccess: r => { if (r.data.url) window.location.href = r.data.url },
  })
  // Returning from Stripe onboarding (?stripe=return) → refresh status.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('stripe') === 'return') refetch()
  }, [refetch])

  if (!data || !data.configured) return null // platform has no Stripe → hide entirely
  if (data.charges_enabled) {
    return (
      <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(76,175,80,0.1)', border: `1px solid ${C.green}`, borderRadius: 10, fontSize: 12, color: C.green, display: 'flex', alignItems: 'center', gap: 8 }}>
        <CheckCircle2 size={14} /> Card payments are active — storefront orders are charged to your Stripe account.
      </div>
    )
  }
  const partial = data.connected && data.details_submitted
  return (
    <div style={{ marginBottom: 16, padding: '12px 16px', background: 'color-mix(in srgb, var(--color-primary) 9%, transparent)', border: `1px solid ${C.amber}`, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>Accept card payments on your storefront</p>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: C.muted }}>
          {data.connected
            ? (partial ? 'Stripe is reviewing your details — payments turn on once approved.' : 'Finish your Stripe setup to start taking cards. Until then, orders are reservations (no payment).')
            : 'Connect Stripe so shoppers pay you directly at checkout. Until then, orders are reservations (you arrange payment).'}
        </p>
      </div>
      <button onClick={() => connect.mutate()} disabled={connect.isPending}
        style={{ padding: '8px 16px', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
        {connect.isPending ? 'Opening Stripe…' : data.connected ? 'Continue Stripe setup' : 'Connect Stripe'}
      </button>
    </div>
  )
}

export function CommercePage() {
  const chartC = useChartColors()
  const qc = useQueryClient()
  const [modal, setModal] = useState<Product | null | 'new'>(null)
  const [activeTab, setActiveTab] = useState<'products' | 'merch' | 'orders' | 'payouts'>('products')
  const [orderSearch, setOrderSearch] = useState('')

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['commerce-products'],
    queryFn: () => commerceApi.listProducts().then(r => r.data),
  })
  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['commerce-orders'],
    queryFn: () => commerceApi.listOrders().then(r => r.data),
  })
  const { data: payouts = [], isLoading: payoutsLoading } = useQuery({
    queryKey: ['commerce-payouts'],
    queryFn: () => commerceApi.listPayouts().then(r => r.data),
    enabled: activeTab === 'payouts',
  })

  const syncToShopify = useMutation({
    mutationFn: (id: string) => commerceApi.syncToShopify(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commerce-products'] }),
  })

  const updateStatus = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status: string; tracking_info?: string; note?: string } }) =>
      commerceApi.updateOrderStatus(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['commerce-orders'] }),
  })

  const revenueChart  = buildRevenueChart(orders)
  const totalRevenue  = orders.reduce((s, o) => s + o.total_amount, 0)
  const inventoryValue = products.reduce((s, p) => s + p.price * p.available_qty, 0)
  const activeProducts = products.filter(p => p.is_available).length
  const shopifyCount  = products.filter(p => p.shopify_product_id).length

  const filteredOrders = orderSearch.trim()
    ? orders.filter(o => o.id.toLowerCase().includes(orderSearch.toLowerCase()))
    : orders

  const latestOrders  = [...orders].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  const newOrderCount = orders.filter(o => !o.status || o.status === 'reserved').length

  return (
    <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', background: C.bg, fontFamily: '"DM Sans", system-ui, sans-serif', color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui, sans-serif' }}>Commerce</h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: C.muted }}>Products, orders, and payouts — direct-to-consumer and wholesale</p>
        </div>
        {activeTab === 'products' && (
          <button onClick={() => setModal('new')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.amber, border: 'none', color: C.bg, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={16} /> Add product
          </button>
        )}
      </div>

      <PaymentsBanner />

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Products',        value: products.length,                icon: <Package size={14} />,     color: C.text },
          { label: 'Available',       value: activeProducts,                  icon: <CheckCircle2 size={14} />, color: C.green },
          { label: 'Orders',          value: orders.length,                   icon: <ShoppingBag size={14} />, color: C.text },
          { label: 'Total revenue',   value: `$${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 0 })}`, icon: <DollarSign size={14} />, color: C.amber },
          { label: 'Inventory value', value: `$${inventoryValue.toFixed(0)}`, icon: <TrendingUp size={14} />,  color: C.muted },
        ].map(s => (
          <div key={s.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
              <span style={{ color: s.color, opacity: 0.7 }}>{s.icon}</span>
              <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</p>
            </div>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Revenue chart */}
      {orders.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <TrendingUp size={14} style={{ color: C.amber }} />
            <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Revenue — Last 8 Months</h2>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: C.faint }}>{orders.length} total orders</span>
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={revenueChart} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={chartC.accent} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={chartC.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={48} />
              <Tooltip
                contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.text }}
                formatter={(v: number, _: string, entry: { payload?: { count?: number } }) => [`$${v.toFixed(2)} (${entry.payload?.count ?? 0} orders)`, 'Revenue']}
                labelStyle={{ color: C.muted }}
                cursor={{ stroke: chartC.grid }}
              />
              <Area type="monotone" dataKey="revenue" stroke={chartC.accent} strokeWidth={2} fill="url(#revenueGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: 20 }}>
        {(['products', 'merch', 'orders', 'payouts'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ padding: '8px 18px', fontSize: 13, fontWeight: activeTab === tab ? 600 : 400, cursor: 'pointer', background: 'none', border: 'none', color: activeTab === tab ? C.amber : C.muted, borderBottom: `2px solid ${activeTab === tab ? C.amber : 'transparent'}`, marginBottom: -1 }}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'products' && products.length > 0 && <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.7 }}>{products.length}</span>}
            {tab === 'orders'   && orders.length > 0   && <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.7 }}>{orders.length}</span>}
            {tab === 'orders'   && newOrderCount > 0   && <span style={{ marginLeft: 5, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: C.amber, color: C.bg }}>{newOrderCount} new</span>}
          </button>
        ))}
      </div>

      {/* Products tab */}
      {activeTab === 'products' && <HarvestFeedPanel />}
      {activeTab === 'products' && (
        productsLoading ? <div style={{ textAlign: 'center', padding: '60px 0', color: C.faint }}>Loading…</div> :
        products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', border: `2px dashed ${C.border}`, borderRadius: 12 }}>
            <ShoppingBag size={32} style={{ color: C.faint, marginBottom: 12, opacity: 0.5 }} />
            <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>No products yet.</p>
            <p style={{ fontSize: 12, color: C.faint, margin: '6px 0 16px' }}>Create products and sync them to Shopify to sell your harvest online.</p>
            <button onClick={() => setModal('new')} style={{ padding: '9px 18px', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add product</button>
          </div>
        ) : (
          <div>
            {shopifyCount > 0 && (
              <div style={{ marginBottom: 12, padding: '9px 14px', background: 'rgba(149,117,205,0.08)', border: '1px solid rgba(149,117,205,0.2)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <ExternalLink size={13} style={{ color: '#9575CD' }} />
                <span style={{ fontSize: 12, color: '#9575CD' }}>{shopifyCount} product{shopifyCount > 1 ? 's' : ''} synced to Shopify</span>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {products.map(p => (
                <div key={p.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.is_available ? C.green : C.faint, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>{p.name}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: C.faint }}>
                      Qty: {p.available_qty}
                      {p.shopify_product_id && <span style={{ marginLeft: 8, color: '#9575CD' }}>Shopify ✓</span>}
                      {!p.is_available && <span style={{ marginLeft: 8, color: C.faint }}>Hidden</span>}
                    </p>
                  </div>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text, flexShrink: 0 }}>${p.price.toFixed(2)}</p>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setModal(p)} style={{ padding: '6px 10px', borderRadius: 7, background: C.card2, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 12 }}>Edit</button>
                    {!p.shopify_product_id && (
                      <button
                        onClick={() => syncToShopify.mutate(p.id)}
                        disabled={syncToShopify.isPending}
                        style={{ padding: '6px 10px', borderRadius: 7, background: 'rgba(149,117,205,0.12)', border: '1px solid rgba(149,117,205,0.3)', color: '#9575CD', cursor: 'pointer', fontSize: 12 }}>
                        → Shopify
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Merch tab */}
      {activeTab === 'merch' && <MerchTab products={products} />}

      {/* Orders tab */}
      {activeTab === 'orders' && (
        ordersLoading ? <div style={{ textAlign: 'center', padding: '60px 0', color: C.faint }}>Loading…</div> :
        orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', border: `2px dashed ${C.border}`, borderRadius: 12 }}>
            <ShoppingBag size={32} style={{ color: C.faint, marginBottom: 12, opacity: 0.5 }} />
            <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>No orders yet.</p>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 14 }}>
              <input value={orderSearch} onChange={e => setOrderSearch(e.target.value)} placeholder="Search by order ID…" style={{ ...inp, width: 200, padding: '7px 12px' }} />
            </div>
            {newOrderCount > 0 && !orderSearch && (
              <div style={{ marginBottom: 12, padding: '9px 14px', background: 'color-mix(in srgb, var(--color-primary) 10%, transparent)', border: `1px solid ${C.amber}`, borderRadius: 8, fontSize: 12, color: C.amber }}>
                {newOrderCount} new order{newOrderCount > 1 ? 's' : ''} awaiting confirmation
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(orderSearch ? filteredOrders : latestOrders).map(o => (
                <OrderCard
                  key={o.id}
                  order={o}
                  busy={updateStatus.isPending}
                  onUpdate={(id, body) => updateStatus.mutate({ id, body })}
                />
              ))}
            </div>
            {filteredOrders.length === 0 && orderSearch && (
              <p style={{ textAlign: 'center', fontSize: 13, color: C.faint, padding: '24px 0' }}>No orders match "{orderSearch}"</p>
            )}
          </div>
        )
      )}

      {/* Payouts tab */}
      {activeTab === 'payouts' && (
        payoutsLoading ? <div style={{ textAlign: 'center', padding: '60px 0', color: C.faint }}>Loading…</div> :
        payouts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', border: `2px dashed ${C.border}`, borderRadius: 12 }}>
            <DollarSign size={32} style={{ color: C.faint, marginBottom: 12, opacity: 0.5 }} />
            <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>No payouts yet.</p>
            <p style={{ fontSize: 12, color: C.faint, margin: '6px 0' }}>Payouts appear after Stripe or Shopify sends funds to your account.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[...payouts].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map(p => (
              <div key={p.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>Payout</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: C.faint }}>{new Date(p.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                </div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.green }}>${p.amount.toFixed(2)}</p>
              </div>
            ))}
          </div>
        )
      )}

      {modal !== null && <ProductModal product={modal === 'new' ? null : modal} onClose={() => setModal(null)} />}
    </main>
  )
}
